/**
 * Initializes FAQ sections: accordion toggles, desktop sticky category sidebar + filter,
 * and mobile category expanders + category picker dropdown.
 *
 * Accordion items are multi-open. Desktop category nav filters main panels (not scroll-to-anchor).
 * Mobile category nav: initial category picker rows, then single-category view with dropdown to switch.
 * Keyboard: accordion triggers are native buttons (Enter/Space toggle).
 */
function initializeFaqSections() {
    const isMobile = document.body.classList.contains('mobile-page');

    document.querySelectorAll('.cmsFaqSection').forEach((section) => {
        initializeFaqSectionAccordions(section);

        if (isMobile) {
            initializeFaqSectionMobileCategories(section);
            initializeFaqSectionCategoryPicker(section);
        } else {
            initializeFaqSectionStickySidebar(section);

            const categoryNav = section.querySelector('.cmsFaqCategoryNav');
            if (categoryNav) {
                initializeFaqSectionCategoryFilter(section, categoryNav);
            }
        }
    });
}

function initializeFaqSectionAccordions(section) {
    section.querySelectorAll('.cmsFaqItemTrigger').forEach((trigger) => {
        const panelId = trigger.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (!panel) {
            return;
        }

        trigger.addEventListener('click', () => {
            const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
            const nextExpanded = !isExpanded;
            trigger.setAttribute('aria-expanded', String(nextExpanded));
            panel.hidden = !nextExpanded;
        });
    });
}

function initializeFaqSectionStickySidebar(section) {
    const sidebar = section.querySelector('.cmsFaqSidebar');
    if (!sidebar) {
        return;
    }

    const updateSidebarStickiness = () => {
        const stickyTopOffset = getFaqStickyTopOffset();
        section.style.setProperty('--cms-faq-sidebar-top', stickyTopOffset + 'px');

        // A sidebar taller than the space below the header would stick with its bottom cut off
        // and stay unreachable until the questions scroll past, so let it scroll with the page instead.
        const sidebarExceedsAvailableHeight = sidebar.offsetHeight > window.innerHeight - stickyTopOffset;
        sidebar.classList.toggle('cmsFaqSidebar--static', sidebarExceedsAvailableHeight);
    };

    updateSidebarStickiness();
    window.addEventListener('resize', updateSidebarStickiness);
    window.addEventListener('scroll', updateSidebarStickiness, { passive: true });
}

function getFaqStickyTopOffset() {
    const mainNavigation = document.getElementById('mainNavigation');
    let offset = mainNavigation ? mainNavigation.getBoundingClientRect().height : 84;

    const pageNavigation = document.getElementById('cms-page-navigation');
    if (pageNavigation) {
        const pageNavigationBottom = pageNavigation.getBoundingClientRect().bottom;
        if (pageNavigationBottom > offset) {
            offset = pageNavigationBottom;
        }
    }

    return offset + 16;
}

function initializeFaqSectionCategoryFilter(section, categoryNav) {
    categoryNav.addEventListener('click', (event) => {
        const navItem = event.target.closest('.cmsFaqCategoryNavItem');
        if (!navItem || !categoryNav.contains(navItem)) {
            return;
        }

        const categoryId = navItem.getAttribute('data-faq-category');
        if (!categoryId) {
            return;
        }

        setSelectedFaqCategoryNavItem(categoryNav, navItem);
        filterFaqCategoryPanels(section, categoryId);
        scrollFaqSectionIntoView(section);
    });
}

function setSelectedFaqCategoryNavItem(categoryNav, selectedItem) {
    categoryNav.querySelectorAll('.cmsFaqCategoryNavItem').forEach((item) => {
        const isSelected = item === selectedItem;
        item.classList.toggle('cmsFaqCategoryNavItem--selected', isSelected);
        if (isSelected) {
            item.setAttribute('aria-current', 'true');
        } else {
            item.removeAttribute('aria-current');
        }
    });
}

function filterFaqCategoryPanels(section, categoryId) {
    const showAllCategories = categoryId === 'all';

    section.querySelectorAll('.cmsFaqCategoryPanel').forEach((panel) => {
        const panelCategoryId = panel.getAttribute('data-faq-category-panel');
        const isVisible = showAllCategories || panelCategoryId === categoryId;
        panel.hidden = !isVisible;

        if (!isVisible) {
            collapseFaqCategoryPanelAccordions(panel);
        }
    });
}

function initializeFaqSectionMobileCategories(section) {
    const categoryList = section.querySelector('.cmsFaqCategoryList');
    if (!categoryList) {
        return;
    }

    categoryList.addEventListener('click', (event) => {
        const trigger = event.target.closest('.cmsFaqCategoryTrigger');
        if (!trigger || !categoryList.contains(trigger)) {
            return;
        }

        if (section.classList.contains('cmsFaqSection--categorySelected')) {
            return;
        }

        const group = trigger.closest('.cmsFaqCategoryGroup');
        const categoryId = group ? group.getAttribute('data-faq-category-group') : null;
        if (!categoryId) {
            return;
        }

        commitFaqMobileCategorySelection(section, categoryId);
    });
}

function initializeFaqSectionCategoryPicker(section) {
    const picker = section.querySelector('.cmsFaqCategoryPicker');
    const menu = section.querySelector('.cmsFaqCategoryPickerMenu');
    if (!picker || !menu) {
        return;
    }

    const closeMenu = () => {
        menu.hidden = true;
        picker.setAttribute('aria-expanded', 'false');
    };

    const openMenu = () => {
        menu.hidden = false;
        picker.setAttribute('aria-expanded', 'true');
    };

    picker.addEventListener('click', (event) => {
        event.stopPropagation();
        const isExpanded = picker.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
            closeMenu();
        } else {
            openMenu();
        }
    });

    menu.addEventListener('click', (event) => {
        event.stopPropagation();

        const option = event.target.closest('.cmsFaqCategoryPickerOption');
        if (!option || !menu.contains(option)) {
            return;
        }

        const categoryId = option.getAttribute('data-faq-category-picker');
        if (!categoryId) {
            return;
        }

        setSelectedFaqCategoryPickerOption(menu, option);
        updateFaqCategoryPickerLabel(picker, option.textContent.trim());
        switchFaqMobileCategorySelection(section, categoryId);
        closeMenu();
        scrollFaqSectionIntoView(section);
    });

    document.addEventListener('click', (event) => {
        if (menu.hidden) {
            return;
        }

        if (picker.contains(event.target) || menu.contains(event.target)) {
            return;
        }

        closeMenu();
    });

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || menu.hidden) {
            return;
        }

        closeMenu();
        picker.focus();
    });
}

function commitFaqMobileCategorySelection(section, categoryId) {
    section.classList.add('cmsFaqSection--categorySelected');

    section.querySelectorAll('.cmsFaqCategoryGroup').forEach((group) => {
        const isSelected = group.getAttribute('data-faq-category-group') === categoryId;
        group.classList.toggle('cmsFaqCategoryGroup--selected', isSelected);

        if (isSelected) {
            revealFaqMobileCategoryContent(group);
        } else {
            hideFaqMobileCategoryContent(group);
        }
    });

    const selectedGroup = section.querySelector('[data-faq-category-group="' + categoryId + '"]');
    if (selectedGroup) {
        syncFaqCategoryPickerToGroup(section, selectedGroup);
    }

    scrollFaqSectionIntoView(section);
}

function switchFaqMobileCategorySelection(section, categoryId) {
    section.querySelectorAll('.cmsFaqCategoryGroup--selected').forEach((group) => {
        const panel = group.querySelector('.cmsFaqCategoryPanel');
        if (panel) {
            collapseFaqCategoryPanelAccordions(panel);
        }
    });

    commitFaqMobileCategorySelection(section, categoryId);
}

function revealFaqMobileCategoryContent(group) {
    const trigger = group.querySelector('.cmsFaqCategoryTrigger');
    const panelId = trigger ? trigger.getAttribute('aria-controls') : null;
    const panel = panelId ? document.getElementById(panelId) : null;
    if (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
    }
    if (panel) {
        panel.hidden = false;
    }
}

function hideFaqMobileCategoryContent(group) {
    const trigger = group.querySelector('.cmsFaqCategoryTrigger');
    const panelId = trigger ? trigger.getAttribute('aria-controls') : null;
    const panel = panelId ? document.getElementById(panelId) : null;
    if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
    }
    if (panel) {
        panel.hidden = true;
        collapseFaqCategoryPanelAccordions(panel);
    }
}

function syncFaqCategoryPickerToGroup(section, group) {
    const picker = section.querySelector('.cmsFaqCategoryPicker');
    const menu = section.querySelector('.cmsFaqCategoryPickerMenu');
    if (!picker) {
        return;
    }

    const categoryId = group.getAttribute('data-faq-category-group');
    const label = group.querySelector('.cmsFaqCategoryLabel');
    if (label) {
        updateFaqCategoryPickerLabel(picker, label.textContent.trim());
    }

    if (menu && categoryId) {
        const option = menu.querySelector('[data-faq-category-picker="' + categoryId + '"]');
        if (option) {
            setSelectedFaqCategoryPickerOption(menu, option);
        }
    }
}

function updateFaqCategoryPickerLabel(picker, label) {
    const labelElement = picker.querySelector('.cmsFaqCategoryPickerLabel');
    if (labelElement) {
        labelElement.textContent = label;
    }
}

function setSelectedFaqCategoryPickerOption(menu, selectedOption) {
    menu.querySelectorAll('.cmsFaqCategoryPickerOption').forEach((option) => {
        const isSelected = option === selectedOption;
        option.classList.toggle('cmsFaqCategoryPickerOption--selected', isSelected);
        option.setAttribute('aria-selected', String(isSelected));
    });
}

function collapseFaqCategoryPanelAccordions(categoryPanel) {
    categoryPanel.querySelectorAll('.cmsFaqItemTrigger').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        const panelId = trigger.getAttribute('aria-controls');
        const answerPanel = panelId ? document.getElementById(panelId) : null;
        if (answerPanel) {
            answerPanel.hidden = true;
        }
    });
}

function scrollFaqSectionIntoView(section) {
    const scrollTop = section.getBoundingClientRect().top + window.scrollY - getFaqStickyTopOffset();

    window.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: 'smooth',
    });
}
